import { useState } from 'react';
import { FlexCenter, Table } from '@elementary/common-ui/components';
import useAccountPermission from '@elementary/common-ui/hooks/useAccountPermission';
import { Typography } from '@mui/material';
import GenerateTokenDialog from 'settings/components/shared/GenerateTokenDialog';
import LoadErrorMessage from '../LoadErrorMessage';
import SettingsSection from '../SettingsSection';
import useRuntime from '../hooks/useRuntime';
import useRuntimeTokensColumns from '../hooks/useRuntimeTokensColumns';

type Props = {
  runtimeId: string;
};

const RuntimeTokens = ({ runtimeId }: Props) => {
  const [isGenerateDialogOpen, setIsGenerateDialogOpen] = useState(false);
  const {
    tokens,
    isLoadingTokens,
    isTokensError,
    refetchTokens,
    createToken,
    isCreatingToken,
  } = useRuntime(runtimeId);
  const hasAccountPermission = useAccountPermission();
  const columns = useRuntimeTokensColumns(runtimeId);

  return (
    <SettingsSection
      title="Tokens"
      actionLabel={
        hasAccountPermission('EDIT_RUNTIMES') ? 'Generate token' : undefined
      }
      onAction={() => setIsGenerateDialogOpen(true)}
    >
      <Table
        data={tokens}
        columns={columns}
        isLoading={isLoadingTokens}
        urlParamsPrefix="runtime_tokens_table"
        emptyStateRenderer={() => (
          <FlexCenter sx={{ width: '100%', py: 2 }}>
            {isTokensError ? (
              <LoadErrorMessage
                message="Couldn't load tokens."
                onRetry={refetchTokens}
              />
            ) : (
              <Typography sx={{ color: 'content.disabled' }}>
                No tokens yet. Generate a token so the runtime can connect to
                Elementary.
              </Typography>
            )}
          </FlexCenter>
        )}
      />

      <GenerateTokenDialog
        isOpen={isGenerateDialogOpen}
        onClose={() => setIsGenerateDialogOpen(false)}
        tokenType="runtime"
        hasDescription={false}
        onCreateToken={async ({ expiresInMinutes }) => {
          const token = await createToken({ expiresInMinutes });
          return { secret: token.secret };
        }}
        isCreating={isCreatingToken}
      />
    </SettingsSection>
  );
};

export default RuntimeTokens;
